import { apiClient, bearer, unwrap } from './client';
import type {
  EngagementListOut,
  EngagementOut,
  EngagementStatus,
  PortalState,
  components,
} from './types';

/** `published` is reachable only through the dedicated `/publish` endpoint,
 *  which requires `audit_run_id`. The generic `/advance` wrapper must not
 *  accept it — callers should hit `publishEngagement` instead. */
export type AdvanceableEngagementStatus = Exclude<EngagementStatus, 'published'>;

export function listEngagementsForCustomer(
  slug: string,
  idToken: string,
): Promise<EngagementListOut> {
  return apiClient
    .GET('/api/customers/{slug}/engagements', {
      params: { path: { slug } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementListOut>);
}

export function getEngagement(id: string, idToken: string): Promise<EngagementOut> {
  return apiClient
    .GET('/api/engagements/{engagement_id}', {
      params: { path: { engagement_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export type CreateEngagementInput = components['schemas']['EngagementCreateIn'];
export type UpdateEngagementInput = components['schemas']['EngagementUpdateIn'];

export function createEngagement(
  slug: string,
  input: CreateEngagementInput,
  idToken: string,
): Promise<EngagementOut> {
  return apiClient
    .POST('/api/customers/{slug}/engagements', {
      params: { path: { slug } },
      body: input,
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export function archiveEngagement(id: string, idToken: string): Promise<EngagementOut> {
  return apiClient
    .POST('/api/engagements/{engagement_id}/archive', {
      params: { path: { engagement_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export function updateEngagement(
  id: string,
  input: UpdateEngagementInput,
  idToken: string,
): Promise<EngagementOut> {
  return apiClient
    .PATCH('/api/engagements/{engagement_id}', {
      params: { path: { engagement_id: id } },
      body: input,
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export function publishEngagement(
  id: string,
  auditRunId: string,
  idToken: string,
  reason?: string,
): Promise<EngagementOut> {
  return apiClient
    .POST('/api/engagements/{engagement_id}/publish', {
      params: { path: { engagement_id: id } },
      body: { audit_run_id: auditRunId, reason: reason ?? null },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export function unpublishEngagement(id: string, idToken: string): Promise<EngagementOut> {
  return apiClient
    .DELETE('/api/engagements/{engagement_id}/publish', {
      params: { path: { engagement_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

export function advanceEngagement(
  id: string,
  toStatus: AdvanceableEngagementStatus,
  idToken: string,
  reason?: string,
): Promise<EngagementOut> {
  return apiClient
    .POST('/api/engagements/{engagement_id}/advance', {
      params: { path: { engagement_id: id } },
      body: { to_status: toStatus, reason: reason ?? null },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

// ── Billing / portal-state controls ─────────────────────────────────

/** Move the engagement through the customer-facing billing lifecycle
 *  (onboarding / processing / awaiting_payment / paid_complete). */
export function setPortalState(
  id: string,
  portalState: PortalState,
  idToken: string,
): Promise<EngagementOut> {
  return apiClient
    .PATCH('/api/engagements/{engagement_id}/portal-state', {
      params: { path: { engagement_id: id } },
      body: { portal_state: portalState },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

/** Store the agree.com payment link the customer pays through. Pass `null`
 *  to clear it. The API rejects any non-agree.com host with a 422. */
export function setAgreePaymentUrl(
  id: string,
  url: string | null,
  idToken: string,
): Promise<EngagementOut> {
  return apiClient
    .PUT('/api/engagements/{engagement_id}/agree-payment-url', {
      params: { path: { engagement_id: id } },
      body: { url },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

/** Attach a previously-uploaded engagement document as the billing invoice.
 *  Pass `null` to detach. The document must already belong to the engagement. */
export function attachBillingInvoice(
  id: string,
  documentId: string | null,
  idToken: string,
): Promise<EngagementOut> {
  return apiClient
    .PUT('/api/engagements/{engagement_id}/billing-invoice', {
      params: { path: { engagement_id: id } },
      body: { document_id: documentId },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

/** Mark the invoice paid — stamps `invoice_paid_at` and unlocks customer
 *  exports once the engagement is published + paid_complete. */
export function markPaid(id: string, idToken: string): Promise<EngagementOut> {
  return apiClient
    .POST('/api/engagements/{engagement_id}/mark-paid', {
      params: { path: { engagement_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}

/** Reverse a mistaken mark-paid: clears `invoice_paid_at` and moves the
 *  engagement back to awaiting_payment. */
export function markUnpaid(id: string, idToken: string): Promise<EngagementOut> {
  return apiClient
    .POST('/api/engagements/{engagement_id}/mark-unpaid', {
      params: { path: { engagement_id: id } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementOut>);
}
